import React, { useEffect, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import { Alert, Box, Button, Chip, Divider, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type {
  LensCard,
  LensCardType,
  LensColumnKind,
  LensDataset,
  LensFilterValues,
  LensPeriod,
  LensResolvedPeriod,
  LensSortConfig,
} from '~/types/leLens';

import { periodLabel } from '../lensPeriodPresets';

import CardPeriodControls from './CardPeriodControls';
import CardSettingDialog from './CardSettingDialog';
import CardStrip from './CardStrip';
import DataTab from './DataTab';
import DatasetFacts from './DatasetFacts';
import WhereChips from './WhereChips';
import { type EditorTab, editorTabStorageKey, initialPanelTab } from './panelInitialTab';
import { type LensPanelSection, type PanelField, type PanelFieldHover } from './panelTypes';
import RowOrder from './tabs/RowOrder';
import { cardShowsSort } from './tabs/useStyleTabState';
import { type PanelDraft, usePanelState } from './usePanelState';

export type LensEditPanelProps = {
  open: boolean;
  card: LensCard | null;
  datasets: ReadonlyArray<LensDataset>;
  cards: ReadonlyArray<LensCard>;
  /** Custom HTML views use their HTML editor; card chart controls are only shown when editing data cards explicitly. */
  allowCardEditing?: boolean;
  onClose: () => void;
  onCancel: () => void;
  /** A card has a local draft that differs from the saved version. Close and
   * cancel defer to the page so it can ask before throwing that draft away. */
  dirty?: boolean;
  onRequestDiscard?: () => void;
  onSave: (card: LensCard) => void | Promise<void>;
  isSaving?: boolean;
  conflict?: React.ReactNode;
  /** Rows for the active card have not arrived yet: skeleton fields, disabled shelves. */
  loading?: boolean;
  /** Columns in the active card result, used while a saved custom dataset has no declaration yet. */
  runtimeColumns?: ReadonlyArray<string>;
  /** The card's contract is blocked; the canvas preview reads "Restricted" instead of drawing. */
  restricted?: boolean;
  rowCount?: number;
  onDeclareNow?: (datasetId: string) => void | Promise<void>;
  declaring?: boolean;
  onNewDataset?: () => void;
  /** "Open in Data catalogue" in a governed dataset's source details: opens the
   *  catalogue's dataset detail screen. Threaded straight through to DatasetFacts. */
  onOpenCatalogueDataset?: (catalogueId: string) => void;
  onFieldHover?: (hover: PanelFieldHover) => void;
  /** The live-preview hand-off: fired on every shelf, dataset or where change. The
   *  page already shapes rows for a card (see `previewShape.ts`); this only hands
   *  it the new datasetId/view to shape and redraw on the canvas. */
  onDraftChange?: (draft: PanelDraft) => void;
  /** True while an inline action elsewhere in the panel (Add column, Edit SQL,
   *  Declare now) has a dry run pending, folded into the Save bar's status line. */
  pendingDryRun?: boolean;
  /** Region 5, owned by C2: Add column / Calculated field / SQL. */
  datasetActionsSlot?: React.ReactNode;
  mergeBanner?: React.ReactNode;
  /** Card type and chart-specific choices supplied by the page. */
  visualContent?: React.ReactNode;
  chartDetailsContent?: React.ReactNode;
  appearanceDetailsContent?: React.ReactNode;
  visualBasicsContent?: React.ReactNode;
  layoutContent?: React.ReactNode;
  /** Sections a just-applied proposal changed. Forced open and ringed for a
   *  few seconds; the page clears this once the ring has shown long enough. */
  ringedSections?: ReadonlyArray<LensPanelSection>;
  /** A card that has never been saved: the status chip and Save bar read
   *  "Draft" instead of "Saved" or "Previewing unsaved changes". */
  isNewCard?: boolean;
  /** A menu or proposal can open the matching Card destination on entry. */
  openSection?: LensPanelSection;
  /** Which editor control initiated the pane, so its destination opens directly. */
  formatFocus?: 'commentary' | 'sort';
  /**
   * The card's current visual type, if it differs from `card.type` because an
   * unsaved Chart edit hasn't been persisted yet. Falls back to
   * `card.type`. Keeps the data shelves and row-order controls aligned with the visual.
   */
  type?: LensCardType;
  /** Series from the last render, for RowOrder's "Sort on". */
  seriesNames?: Array<string>;
  /** The card's current row order (config.sort). */
  sort?: LensSortConfig;
  /** Fired with the next row order on every RowOrder change, live. */
  onSortChange?: (sort: LensSortConfig | undefined) => void;
  onRelabelField?: (field: PanelField, label: string) => void;
  onChangeFieldKind?: (field: PanelField, kind: Exclude<LensColumnKind, 'calculated'>) => void;
  onRemoveField?: (field: PanelField) => void;
  /** The card strip's inline title, edited only in the header. */
  title?: string;
  onTitleChange?: (title: string) => void;
  /** The card strip's kebab: the exact handlers the canvas card's own menu
   *  already calls (`LensCardShell.tsx`), threaded through so Delete,
   *  Duplicate and Move to section are reachable without closing the pane. */
  onDeleteCard?: () => void;
  onDuplicateCard?: () => void;
  onMoveCardToSection?: Array<{ label: string; onMove: () => void }>;
  /**
   * F8's "Remove a dataset": fired only once this panel has already decided
   * the removal is allowed (no card reads the dataset). A blocked attempt
   * never reaches this callback; the blocking cards are shown inline
   * instead. The caller (LeLensPage) owns confirming and persisting it.
   */
  onRemoveDataset?: (datasetId: string) => void;
  /** Forces a width, for the hosting Drawer and visual harness. Otherwise computed from viewport width. */
  widthOverride?: number | 'full';
  /** The Lens the card belongs to: lets Data's filters list a field's values. */
  lensId?: string;
  /** The board's active filter values, so listed values match the period on screen. */
  filterValues?: LensFilterValues;
  /** The board's own window and comparison, which a card follows unless it pins its own. */
  boardPeriod?: LensPeriod;
  resolvedPeriod?: LensResolvedPeriod;
};

type SectionHeaderRowProps = {
  icon: React.ReactNode;
  label: string;
  contentId: string;
  open: boolean;
  onToggle: () => void;
  ringed?: boolean;
  summary?: string;
  children?: React.ReactNode;
};

/**
 * A compact route to focused card settings, shared by Data and Appearance.
 */
function SectionHeaderRow({
  children,
  contentId,
  icon,
  label,
  onToggle,
  open,
  ringed,
  summary,
}: SectionHeaderRowProps) {
  return (
    <CardSettingDialog
      contentId={contentId}
      icon={icon}
      label={label}
      onClose={onToggle}
      onOpen={onToggle}
      open={open}
      ringed={ringed}
      summary={summary ?? ''}
    >
      {children}
    </CardSettingDialog>
  );
}

/**
 * Sort's one-line summary reads the row order already passed to this panel.
 */
function sortRowSummary(sort: LensSortConfig | undefined): string {
  if (!sort || sort.by === 'none') {
    return 'As returned';
  }
  const by = sort.by === 'label' ? 'Axis label' : sort.series || 'Value';
  return `${by}, ${sort.direction === 'asc' ? 'ascending' : 'descending'}`;
}

/**
 * The persistent right pane: Data includes card filters and source details;
 * Chart owns visual structure; Appearance owns formatting and placement.
 */
export default function LensEditPanel({
  allowCardEditing = true,
  cards,
  card,
  chartDetailsContent,
  conflict,
  appearanceDetailsContent,
  datasetActionsSlot,
  datasets,
  declaring,
  dirty = false,
  formatFocus,
  isNewCard,
  isSaving,
  layoutContent,
  loading,
  mergeBanner,
  onCancel,
  onChangeFieldKind,
  onClose,
  onDeclareNow,
  onDeleteCard,
  onDraftChange,
  onDuplicateCard,
  onFieldHover,
  onMoveCardToSection,
  onNewDataset,
  onOpenCatalogueDataset,
  onRelabelField,
  onRemoveDataset,
  onRemoveField,
  onRequestDiscard,
  onSave,
  onSortChange,
  onTitleChange,
  open,
  openSection,
  pendingDryRun,
  restricted,
  ringedSections,
  rowCount,
  runtimeColumns,
  seriesNames,
  sort,
  title,
  type,
  visualContent,
  visualBasicsContent,
  widthOverride,
  lensId,
  filterValues,
  boardPeriod,
  resolvedPeriod,
}: LensEditPanelProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const width = widthOverride ?? (isXl ? 440 : isLgUp ? 400 : 'full');
  const isSheet = width === 'full';

  const state = usePanelState({ card: type ? { ...card, type } : card, datasets, onDraftChange, runtimeColumns });
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleFieldRef = useRef<HTMLInputElement | null>(null);
  const inlineMenuOpenRef = useRef(false);
  // An insight answers in prose, so Data's shelves and fields do not apply.
  // Chart can still turn it into a visual and Appearance can style the card.
  const isInsight = card?.type === 'insight';
  const rememberedEditorTab = (() => {
    try {
      return sessionStorage.getItem(editorTabStorageKey(lensId));
    } catch {
      return null;
    }
  })();
  const [activeTab, setActiveTab] = useState<EditorTab>(() => {
    return initialPanelTab({
      isUndeclared: state.isUndeclared,
      isNewCard,
      openSection,
      storedTab: rememberedEditorTab,
    });
  });
  const changeTab = (next: EditorTab) => {
    setActiveTab(next);
    try {
      sessionStorage.setItem(editorTabStorageKey(lensId), next);
    } catch {
      /* Preferences are optional. */
    }
  };
  const [layoutOpen, setLayoutOpen] = useState(openSection === 'layout');
  const [sourceOpen, setSourceOpen] = useState(state.isUndeclared || rememberedEditorTab === 'source');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Sort only shows for some card types, so its open state lives here.
  const [sortOpen, setSortOpen] = useState(formatFocus === 'sort');
  useEffect(() => {
    if (openSection === 'layout' || openSection === 'format') {
      setActiveTab('appearance');
      setLayoutOpen(openSection === 'layout');
    } else if (openSection === 'visual') {
      setActiveTab('chart');
    }
  }, [openSection]);
  useEffect(() => {
    if (state.isUndeclared) setSourceOpen(true);
  }, [state.isUndeclared]);
  useEffect(() => {
    if (formatFocus) {
      setSortOpen(formatFocus === 'sort');
      setActiveTab(formatFocus === 'sort' ? 'data' : 'appearance');
    }
  }, [formatFocus]);

  // A proposal just applied from Ask mode forces its section(s) open, even
  // over a collapse the reader chose earlier: the point of the ring is to
  // show the change without a click. Keyed on the joined section list rather
  // than the array reference, since a caller may pass a fresh array each
  // render for the same contents.
  const ringedKey = (ringedSections ?? []).join(',');
  useEffect(() => {
    if (!ringedSections?.length) {
      return;
    }
    setActiveTab(
      ringedSections.includes('data')
        ? 'data'
        : ringedSections.includes('layout') || ringedSections.includes('format')
          ? 'appearance'
          : 'chart',
    );
    if (ringedSections.includes('layout')) setLayoutOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringedKey]);

  // A brand-new card opens on Data since there is nothing to draw yet. The
  // first time canSave flips from false to true, show the drawn chart and
  // shape choices immediately.
  const newCardFormatSwitchRef = useRef(false);
  useEffect(() => {
    if (!isNewCard || !state.canSave || newCardFormatSwitchRef.current) {
      return;
    }
    newCardFormatSwitchRef.current = true;
    setActiveTab('chart');
  }, [isNewCard, state.canSave]);

  const save = async () => {
    const next = state.buildNextCard();
    if (next) {
      await onSave(next);
    }
  };

  const cancel = () => {
    if (dirty) {
      onRequestDiscard?.();
      return;
    }
    state.resetDraft();
    onCancel();
  };

  // Closing keeps the parent's draft; only the explicit Discard action resets it.
  const closePanel = () => onClose();

  // Escape used to be handled by an onKeyDown on the panel's own root Box,
  // which only ever fires when the keydown's bubble path runs through that
  // Box, i.e. focus is somewhere inside the panel. Once focus moves to the
  // canvas (or anywhere else), pressing Escape did nothing (confirmed by C3's
  // harness, worked around there with an explicit close-button click rather
  // than fixed). A document-level listener sees every Escape press while the
  // panel is open regardless of focus location; a ref holds the latest
  // closePanel so the listener itself only needs to be re-attached when
  // `open` changes, not on every render. The inlineMenuOpenRef check is
  // unchanged: Escape closes an inline section before it closes the panel.
  const closePanelRef = useRef(closePanel);
  useEffect(() => {
    closePanelRef.current = closePanel;
  });
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        inlineMenuOpenRef.current ||
        document.querySelector('[data-lens-card-settings-dialog]')
      ) {
        return;
      }
      closePanelRef.current();
    };
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [open]);

  // Every open moves focus into the pane, not only the below-desktop sheet:
  // without this, Enter on the card's "Edit card" menu item opened the pane
  // but left focus on the trigger button, so keyboard Tab never reached a
  // single control inside it. Desktop lands on the card strip's title, the
  // first meaningful control; the sheet
  // falls back to the panel root, matching the entry point and
  // return action unambiguously for keyboard users instead of leaving focus
  // on an obscured canvas control.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (isSheet || !titleFieldRef.current) {
      panelRef.current?.focus();
      return;
    }
    titleFieldRef.current.focus();
  }, [isSheet, open]);

  if (!open || !card) {
    return null;
  }

  const statusLabel = isNewCard ? 'New chart' : dirty || isSaving ? 'Applying…' : 'Saved';
  // The Save bar's own status line uses "Draft" rather than the header
  // chip's "New chart": a shorter word for a smaller caption, same meaning.
  const saveBarStatus = isNewCard ? 'Draft' : dirty || isSaving ? 'Applying…' : 'Saved';
  const chipSx = { bgcolor: alpha(tokens.accent, 0.1), color: tokens.accent };
  const effectiveCardType = type ?? card.type;
  const showsSort = cardShowsSort(effectiveCardType);
  const filterCount = state.draft.view.where?.length ?? 0;
  const filterSummary = `${
    state.draft.period === 'none'
      ? 'No date range'
      : state.draft.period
        ? 'Card date range'
        : boardPeriod
          ? `Follows Lens · ${periodLabel(boardPeriod)}`
          : 'Follows Lens'
  } · ${filterCount ? `${filterCount} chart filter${filterCount === 1 ? '' : 's'}` : 'No chart filters'}`;
  // Shared by Data's field browser, filters and source details.
  const declareNow = state.dataset ? () => onDeclareNow?.(state.dataset.id) : undefined;

  return (
    <Box
      aria-label="Card editor"
      aria-modal={isSheet || undefined}
      component="aside"
      ref={panelRef}
      role={isSheet ? 'dialog' : 'complementary'}
      sx={{
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
        flex: 1,
        outline: 0,
        position: 'relative',
        ...(isSheet && widthOverride === undefined
          ? {
              boxShadow: 24,
              height: '100dvh',
              inset: 0,
              position: 'fixed',
              width: '100vw',
              zIndex: theme.zIndex.modal,
            }
          : {}),
        transition: reducedMotion ? 'none' : theme.transitions.create(['width']),
        width: isSheet ? (widthOverride === 'full' ? '100%' : '100vw') : width,
      }}
    >
      <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', px: 3, py: 1.5 }}>
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
          <CardStrip
            cardType={effectiveCardType}
            moveOptions={onMoveCardToSection}
            onDelete={onDeleteCard}
            onDuplicate={onDuplicateCard}
            onTitleChange={onTitleChange ?? (() => undefined)}
            title={title ?? card.title}
            titleInputRef={titleFieldRef}
          />
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ mt: 1, minWidth: 0, '& .MuiChip-root': { maxWidth: '60%' } }}>
          {!isInsight && <Chip label={state.dataset?.name ?? 'No dataset yet'} size="small" sx={chipSx} />}
          <Chip label={statusLabel} size="small" sx={chipSx} />
        </Stack>
      </Box>

      {allowCardEditing && (
        <>
          <Tabs
            value={activeTab}
            onChange={(_event, next: EditorTab) => changeTab(next)}
            variant="fullWidth"
            aria-label="Chart editor sections"
            sx={{
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: 40,
              '& .MuiTabs-indicator': { backgroundColor: tokens.accent },
              '& .MuiTab-root': {
                fontSize: 13,
                fontWeight: 600,
                minHeight: 40,
                minWidth: 0,
                px: 1,
                textTransform: 'none',
              },
            }}
          >
            <Tab id="lens-editor-tab-data" aria-controls="lens-editor-data" label="Data" value="data" />
            <Tab id="lens-editor-tab-chart" aria-controls="lens-editor-chart" label="Chart" value="chart" />
            <Tab
              id="lens-editor-tab-appearance"
              aria-controls="lens-editor-appearance"
              label="Appearance"
              value="appearance"
            />
          </Tabs>
          <Box
            role="tabpanel"
            id="lens-editor-data"
            aria-labelledby="lens-editor-tab-data"
            hidden={activeTab !== 'data'}
            sx={{
              flex: 1,
              minHeight: 0,
              display: activeTab === 'data' ? 'flex' : 'none',
              flexDirection: 'column',
              px: 3,
              py: 2,
            }}
          >
            <DataTab
              cards={cards}
              datasets={datasets}
              declaring={declaring}
              isInsight={isInsight}
              loading={loading}
              onChangeFieldKind={onChangeFieldKind}
              onDeclareNow={declareNow}
              onFieldHover={onFieldHover}
              onMenuOpenChange={(isOpen) => {
                inlineMenuOpenRef.current = isOpen;
              }}
              onNewDataset={onNewDataset}
              onRelabelField={onRelabelField}
              onRemoveDataset={onRemoveDataset}
              onRemoveField={onRemoveField}
              filtersContent={
                <SectionHeaderRow
                  contentId="lens-editor-date-and-filters"
                  icon={<FilterAltOutlinedIcon fontSize="small" />}
                  label="Date & filters"
                  onToggle={() => setFiltersOpen((current) => !current)}
                  open={filtersOpen}
                  ringed={ringedSections?.includes('data')}
                  summary={filterSummary}
                >
                  <Stack spacing={2.5}>
                    <Alert severity="info">Lens filters apply first. Changes here affect only this card.</Alert>
                    {boardPeriod && (
                      <Box>
                        <Typography sx={{ fontWeight: 600, mb: 1.5 }} variant="subtitle2">
                          Reporting period
                        </Typography>
                        <CardPeriodControls boardPeriod={boardPeriod} resolved={resolvedPeriod} state={state} />
                      </Box>
                    )}
                    <Divider />
                    <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
                      Chart filters
                    </Typography>
                    <WhereChips
                      declaring={declaring}
                      filterValues={filterValues}
                      lensId={lensId}
                      onDeclareNow={declareNow}
                      state={state}
                    />
                  </Stack>
                </SectionHeaderRow>
              }
              sortContent={
                showsSort ? (
                  <SectionHeaderRow
                    contentId="lens-panel-section-sort"
                    icon={<SwapVertOutlinedIcon fontSize="small" />}
                    label="Sort rows"
                    onToggle={() => setSortOpen((current) => !current)}
                    open={sortOpen}
                    summary={sortRowSummary(sort)}
                  >
                    <RowOrder
                      busy={loading}
                      cardId={card.id}
                      onChange={onSortChange}
                      seriesNames={seriesNames ?? []}
                      sort={sort}
                      type={effectiveCardType}
                    />
                  </SectionHeaderRow>
                ) : undefined
              }
              sourceContent={
                state.dataset ? (
                  <Stack spacing={1.5}>
                    <DatasetFacts
                      dataset={state.dataset}
                      declaring={declaring}
                      mergeBanner={mergeBanner}
                      onDeclareNow={declareNow}
                      onOpenCatalogueDataset={onOpenCatalogueDataset}
                      restricted={restricted}
                      rowCount={rowCount}
                    />
                    {datasetActionsSlot && (
                      <>
                        <Divider />
                        <Typography color="text.secondary" variant="caption">
                          Manage dataset
                        </Typography>
                        {datasetActionsSlot}
                      </>
                    )}
                  </Stack>
                ) : undefined
              }
              sourceOpen={sourceOpen}
              onSourceToggle={() => setSourceOpen((current) => !current)}
              state={state}
            />
          </Box>
          <Box
            role="tabpanel"
            id="lens-editor-chart"
            aria-labelledby="lens-editor-tab-chart"
            hidden={activeTab !== 'chart'}
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: activeTab === 'chart' ? 'block' : 'none' }}
          >
            <Box sx={{ px: 3, py: 2 }}>
              {visualContent}
              {chartDetailsContent}
            </Box>
          </Box>
          <Box
            role="tabpanel"
            id="lens-editor-appearance"
            aria-labelledby="lens-editor-tab-appearance"
            hidden={activeTab !== 'appearance'}
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: activeTab === 'appearance' ? 'block' : 'none' }}
          >
            <Box sx={{ px: 3, py: 2 }}>{visualBasicsContent}</Box>
            <Box sx={{ px: 3 }}>
              <SectionHeaderRow
                contentId="lens-panel-size-and-placement"
                icon={<ViewQuiltOutlinedIcon fontSize="small" />}
                label="Size & placement"
                onToggle={() => setLayoutOpen((current) => !current)}
                open={layoutOpen}
                ringed={ringedSections?.includes('layout')}
                summary="Width, height and section"
              >
                {layoutContent}
              </SectionHeaderRow>
            </Box>
            <Box sx={{ px: 3, pb: 2 }}>{appearanceDetailsContent}</Box>
          </Box>

          <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', minHeight: 56, px: 3, py: 1.5 }}>
            <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
              {isNewCard && (
                <Button disabled={isSaving} onClick={cancel}>
                  Discard
                </Button>
              )}
              {conflict ? (
                <Box sx={{ flex: 1, minWidth: 0 }}>{conflict}</Box>
              ) : (
                <Typography color="text.secondary" sx={{ flex: 1, fontSize: 12, textAlign: 'center' }} variant="caption">
                  {pendingDryRun ? 'Running a dry run…' : saveBarStatus}
                </Typography>
              )}
              <Stack alignItems="center" direction="row" spacing={1}>
                {isNewCard && (
                  <Button disabled={isSaving || !state.canSave} onClick={save} variant="contained">
                    {isSaving ? 'Adding…' : 'Add chart'}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}

export { usePanelState } from './usePanelState';
export type { PanelDraft } from './usePanelState';
